import React, { useState, useEffect, useRef, useMemo } from 'react';
import Select from 'react-select';
import InfoTip from '../../InfoTip';
import { talentService, talentScheduleService } from '../../../api/apiService';
import { useAuth } from '../../../contexts/AuthContext';
import { customStyles } from '../../../helpers/StyleUtils';
import { talentLocationShorts, talentLocationLabel, locationOptionsFrom } from '../../../helpers/locationDisplay';
import '../../../App.css';
import './TalentListSimple.css';

const TAX_TIP = "Der hier angegebene Steuersatz wird automatisch in deine Gutschrift übernommen.";
const VAT_TIP = "Info – in Deutschland genügt entweder die\n• Angabe der Steuernummer oder der Umsatzsteuer-ID. Eine von beiden ist jedoch Pflicht.";

// Flat line icons (inherit color via currentColor).
const svgBase = { fill: 'none', stroke: 'currentColor', strokeWidth: 2, strokeLinecap: 'round', strokeLinejoin: 'round' };
const Icon = {
  link: ({ size = 12, ...p }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" {...svgBase} style={{ verticalAlign: 'middle' }} {...p}>
      <path d="M10 13a5 5 0 0 0 7.07 0l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" />
      <path d="M14 11a5 5 0 0 0-7.07 0l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" />
    </svg>
  ),
  check: ({ size = 14, ...p }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" {...svgBase} strokeWidth={2.5} style={{ verticalAlign: 'middle' }} {...p}>
      <polyline points="20 6 9 17 4 12" />
    </svg>
  ),
  warn: ({ size = 14, ...p }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" {...svgBase} style={{ verticalAlign: 'middle' }} {...p}>
      <path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
      <line x1="12" y1="9" x2="12" y2="13" />
      <line x1="12" y1="17" x2="12.01" y2="17" />
    </svg>
  ),
  chevron: ({ size = 14, open = false, ...p }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" {...svgBase} style={{ verticalAlign: 'middle', transform: open ? 'rotate(180deg)' : 'none', transition: 'transform 0.15s' }} {...p}>
      <polyline points="6 9 12 15 18 9" />
    </svg>
  ),
  dotFilled: ({ size = 10, ...p }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" style={{ verticalAlign: 'middle' }} {...p}>
      <circle cx="12" cy="12" r="7" />
    </svg>
  ),
  dotEmpty: ({ size = 10, ...p }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" {...svgBase} style={{ verticalAlign: 'middle' }} {...p}>
      <circle cx="12" cy="12" r="7" />
    </svg>
  ),
};

/**
 * Agency & Talent Billing Management
 * - Agencies: Group talents by Agentur, update agency billing info for all
 * - Individual Talents: Talents without agency, update their own VAT/billing
 */
function AgencyManagement() {
  const { currentUser, userRole } = useAuth();
  const [talents, setTalents] = useState([]);
  const [agencies, setAgencies] = useState([]);
  const [talentsWithoutAgency, setTalentsWithoutAgency] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Selection state
  const [selectedAgency, setSelectedAgency] = useState(null);
  const [selectedTalent, setSelectedTalent] = useState(null);
  const [editMode, setEditMode] = useState(null); // 'agency' or 'talent'

  const [editForm, setEditForm] = useState({});
  const [talentForms, setTalentForms] = useState({}); // Individual talent VAT forms when bill_separately is checked
  const [expandedTalentId, setExpandedTalentId] = useState(null); // which agency talent row is open
  const [savingTalentId, setSavingTalentId] = useState(null);
  const [talentSaveMsg, setTalentSaveMsg] = useState({}); // per-talent save message keyed by id
  const [linkedGroups, setLinkedGroups] = useState({}); // talentId -> [ids linked together]
  const [linkedPreview, setLinkedPreview] = useState({}); // talentId -> [{id,name,fn}] resolved on expand
  const [previewLoadingId, setPreviewLoadingId] = useState(null);
  const [agencyDetailLoading, setAgencyDetailLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [saveMessage, setSaveMessage] = useState('');
  // Which studio to narrow the lists to; null = all
  const [filterLocation, setFilterLocation] = useState(null);

  // Full talent records keyed by id — the light list (getTalentsList) strips
  // relatedTalent, so we preload the full list which reliably carries links.
  const fullByIdRef = useRef({});

  // Talent-level billing fields (their own VAT/address, used for the talent CN)
  const TALENT_BILLING_KEYS = ['vat_status', 'tax_id', 'vat_id', 'bank_account', 'country', 'company_name', 'address'];

  // Pull just the billing fields off a talent record into a form object.
  const pickBilling = (t) => {
    const out = {};
    TALENT_BILLING_KEYS.forEach((k) => { out[k] = t?.[k] || ''; });
    return out;
  };
  const formHasData = (form) => TALENT_BILLING_KEYS.some((k) => (form?.[k] || '') !== '');

  // Check if user is admin
  const isAdmin = userRole === 'superadmin' || userRole === 'admin' || userRole === 'clientService' || userRole === 'planning' || userRole === 'finance';

  // Load talents and group by agency
  useEffect(() => {
    const loadTalents = async () => {
      setLoading(true);
      setError(null);
      try {
        let talentsToProcess = [];

        if (isAdmin) {
          // Admin: load all talents
          const res = await talentService.getTalentsList(1, 5000, '');
          talentsToProcess = res.data.talents || [];
        } else {
          // Talent user: only use their linked talents
          const userTalentIds = currentUser?.talents?.map(t => t.id || t) || [];

          if (userTalentIds.length === 0) {
            setError('No talents linked to your account');
            setLoading(false);
            return;
          }

          // Fetch full talent data for each linked talent
          const talentPromises = userTalentIds.map(id =>
            talentService.getTalentById(id).catch(err => {
              console.error(`Error fetching talent ${id}:`, err);
              return null;
            })
          );
          const results = await Promise.all(talentPromises);
          talentsToProcess = results
            .filter(r => r && r.data)
            .map(r => r.data);
        }

        setTalents(talentsToProcess);

        // Separate talents with and without agency
        const withAgency = [];
        const withoutAgency = [];

        talentsToProcess.forEach(t => {
          const agencyName = t.Agentur;
          if (agencyName && agencyName.trim() !== '') {
            withAgency.push(t);
          } else {
            withoutAgency.push(t);
          }
        });

        // Group talents by agency (Agentur)
        const agencyMap = {};
        withAgency.forEach(t => {
          const agencyName = t.Agentur;
          if (!agencyMap[agencyName]) {
            agencyMap[agencyName] = {
              name: agencyName,
              talents: [],
              // Take agency info from first talent with this agency
              agency_vat_status: t.agency_vat_status || '',
              agency_tax_id: t.agency_tax_id || '',
              agency_vat_id: t.agency_vat_id || '',
              agency_bank_account: t.agency_bank_account || '',
              agency_email: t.agency_email || '',
              agency_address: t.agency_address || '',
              AP: t.AP || '',
              is_agency: t.is_agency === 1 || t.is_agency === true,
              agency_bill_separately: t.agency_bill_separately === 1 || t.agency_bill_separately === true,
              agency_own_account: t.agency_own_account === 1 || t.agency_own_account === true,
            };
          }
          agencyMap[agencyName].talents.push(t);
        });

        // Convert to array and sort by name
        const agencyList = Object.values(agencyMap).sort((a, b) =>
          a.name.localeCompare(b.name)
        );
        setAgencies(agencyList);
        setTalentsWithoutAgency(withoutAgency);

      } catch (err) {
        console.error('Error loading talents:', err);
        setError(err.message || 'Failed to load talents');
      } finally {
        setLoading(false);
      }
    };
    loadTalents();
  }, [currentUser, userRole, isAdmin]);

  // Preload the FULL talent list to get relatedTalent links (the light list
  // used above omits them). Keyed by id for quick lookup.
  useEffect(() => {
    (async () => {
      try {
        const res = await talentService.getAllTalents(1, 5000);
        const list = res?.data?.talents || res?.data || [];
        const map = {};
        (Array.isArray(list) ? list : []).forEach((t) => { if (t && t.id != null) map[String(t.id)] = t; });
        fullByIdRef.current = map;
      } catch (e) {
        console.warn('Could not preload full talents for relationships:', e);
      }
    })();
  }, []);

  // ── Location filter ─────────────────────────────────────────────────────────
  // Only the studios that actually occur among the loaded talents
  const locationOptions = useMemo(
    () => locationOptionsFrom(talents, talentLocationShorts),
    [talents]
  );

  const talentAtLocation = (t) =>
    !filterLocation || talentLocationShorts(t).includes(filterLocation.value);

  /** Studios of an agency's talents, deduped — "EB, TR". */
  const agencyLocationLabel = (agency) => {
    const shorts = new Set((agency.talents || []).flatMap(talentLocationShorts));
    return shorts.size > 0 ? [...shorts].sort().join(', ') : '—';
  };

  // An agency stays visible when at least one of its talents works at the studio. The agency
  // object itself is NOT filtered — saving must still apply to all of its talents.
  const visibleAgencies = useMemo(
    () => (filterLocation ? agencies.filter(a => (a.talents || []).some(talentAtLocation)) : agencies),
    [agencies, filterLocation]
  );

  const visibleIndividuals = useMemo(
    () => talentsWithoutAgency.filter(talentAtLocation),
    [talentsWithoutAgency, filterLocation]
  );

  // Select an agency for editing
  const handleSelectAgency = (agency) => {
    setSelectedAgency(agency);
    setSelectedTalent(null);
    setEditMode('agency');
    const firstTalent = agency.talents && agency.talents[0];
    setEditForm({
      Agentur: agency.name || '',
      agency_vat_status: agency.agency_vat_status || '',
      agency_tax_id: agency.agency_tax_id || '',
      agency_vat_id: agency.agency_vat_id || '',
      agency_bank_account: agency.agency_bank_account || '',
      agency_email: agency.agency_email || '',
      agency_address: agency.agency_address || '',
      AP: agency.AP || '',
      is_agency: agency.is_agency || false,
      agency_bill_separately: agency.agency_bill_separately || false,
      agency_own_account: agency.agency_own_account || false,
      vat_status: firstTalent?.vat_status || '',
      tax_id: firstTalent?.tax_id || '',
      vat_id: firstTalent?.vat_id || '',
      bank_account: firstTalent?.bank_account || '',
      country: firstTalent?.country || '',
      company_name: firstTalent?.company_name || '',
      address: firstTalent?.address || '',
    });

    // Seed per-talent forms from the light-list values first (instant paint).
    const baseForms = {};
    (agency.talents || []).forEach((t) => { baseForms[t.id] = pickBilling(t); });
    setTalentForms(baseForms);
    setExpandedTalentId(null);
    setTalentSaveMsg({});
    setLinkedGroups({});
    setSaveMessage('');

    // Fetch full billing records + each talent's related_talent_ids (from the
    // schedule endpoint — the same links the shared calendar uses), then group
    // linked talents and cross-fill: if any linked record has data, all share it.
    setAgencyDetailLoading(true);
    (async () => {
      try {
        const talentList = agency.talents || [];
        const [fulls, relLists] = await Promise.all([
          Promise.all(talentList.map((t) => talentService.getTalentById(t.id).then((r) => r?.data).catch(() => null))),
          Promise.all(talentList.map((t) => getRelatedTalentIds(t.id))),
        ]);
        const byId = {};
        fulls.forEach((t) => { if (t) byId[String(t.id)] = t; });
        const relById = {};
        talentList.forEach((t, i) => { relById[String(t.id)] = relLists[i] || []; });

        // Build billing forms from full data (fall back to light record).
        const forms = {};
        talentList.forEach((t) => { forms[t.id] = pickBilling(byId[String(t.id)] || t); });

        // Linked group = the talent + its OWN related_talent_ids (purely from
        // the relationship data — independent of the agency grouping).
        const linkMap = {};
        talentList.forEach((t) => {
          const id = String(t.id);
          linkMap[id] = [...new Set([id, ...(relById[id] || []).map(String)])];
        });

        // Cross-fill: if a talent has no billing but a linked talent we loaded
        // does, copy it in (so linked rows show the shared data).
        talentList.forEach((t) => {
          const id = String(t.id);
          if (formHasData(forms[id]) || linkMap[id].length < 2) return;
          const donor = linkMap[id].find((gid) => byId[gid] && formHasData(pickBilling(byId[gid])));
          if (donor) forms[id] = pickBilling(byId[donor]);
        });

        setTalentForms(forms);
        setLinkedGroups(linkMap);
      } catch (e) {
        console.warn('Could not load linked talent details:', e);
      } finally {
        setAgencyDetailLoading(false);
      }
    })();
  };

  // Select an individual talent for editing
  const handleSelectTalent = (talent) => {
    setSelectedTalent(talent);
    setSelectedAgency(null);
    setEditMode('talent');
    setEditForm({
      Agentur: talent.Agentur || '',
      vat_status: talent.vat_status || '',
      tax_id: talent.tax_id || '',
      vat_id: talent.vat_id || '',
      bank_account: talent.bank_account || '',
      country: talent.country || '',
      company_name: talent.company_name || '',
      address: talent.address || '',
      // Agency data (shown if this talent is / acts as an agency)
      is_agency: talent.is_agency === 1 || talent.is_agency === true,
      agency_bill_separately: talent.agency_bill_separately === 1 || talent.agency_bill_separately === true,
      agency_own_account: talent.agency_own_account === 1 || talent.agency_own_account === true,
      AP: talent.AP || '',
      agency_vat_status: talent.agency_vat_status || '',
      agency_tax_id: talent.agency_tax_id || '',
      agency_vat_id: talent.agency_vat_id || '',
      agency_bank_account: talent.agency_bank_account || '',
      agency_email: talent.agency_email || '',
      agency_address: talent.agency_address || '',
    });
    setSaveMessage('');
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setEditForm(prev => {
      const updated = { ...prev, [name]: type === 'checkbox' ? checked : value };
      // Mutually exclusive billing modes: separate CNs vs. agency bills on own account
      if (name === 'agency_bill_separately' && checked) updated.agency_own_account = false;
      if (name === 'agency_own_account' && checked) updated.agency_bill_separately = false;
      return updated;
    });
  };

  // Edit one talent's own billing form (bill-separately mode)
  const handleTalentFormChange = (talentId, e) => {
    const { name, value } = e.target;
    setTalentForms(prev => ({ ...prev, [talentId]: { ...prev[talentId], [name]: value } }));
  };

  // Get a talent's linked talent ids from the schedule endpoint, which returns
  // schedule.related_talent_ids (the bidirectional group created by
  // "Link talents" / createBulkRelationships). This is the SAME source the
  // shared calendar uses.
  const getRelatedTalentIds = async (talentId) => {
    try {
      const today = new Date();
      const start = today.toISOString().slice(0, 10);
      const end = new Date(today.getTime() + 7 * 86400000).toISOString().slice(0, 10);
      const res = await talentScheduleService.getTalentScheduleByRange(talentId, start, end);
      const s = res?.data?.schedule || {};
      const rel =
        s.related_talent_ids ||
        s.relatedTalentIds ||
        s.related_talents ||
        res?.data?.related_talent_ids ||
        [];
      return (Array.isArray(rel) ? rel : []).map((r) => String(r && typeof r === 'object' ? (r.id ?? r.talent_id ?? r.value) : r));
    } catch (e) {
      console.warn(`Could not read related_talent_ids for ${talentId}:`, e);
      return [];
    }
  };

  const resolveLinkedInfo = async (startId) => {
    const ids = new Set([String(startId)]);
    (await getRelatedTalentIds(startId)).forEach((rid) => ids.add(String(rid)));
    return [...ids]
      .filter((id) => id && id !== 'undefined' && id !== 'null')
      .map((id) => {
        const t = fullByIdRef.current[String(id)];
        return { id, name: t?.Name || t?.name || `ID ${id}`, fn: t?.Funktion || t?.function || '' };
      });
  };

  const propagateBillingToLinked = async (startId, billingData) => {
    const members = await resolveLinkedInfo(startId);
    const updated = [];
    let ok = 0;
    let fail = 0;
    for (const m of members) {
      try { await talentService.updateTalent(m.id, billingData); ok++; updated.push(m); }
      catch (e) { console.error(`Error updating talent ${m.id}:`, e); fail++; }
    }
    return { allIds: members.map((m) => m.id), ok, fail, updated };
  };

  // "Name (Function)" list for confirmation messages.
  const describeUpdated = (updated) =>
    (updated || []).map((u) => `${u.name}${u.fn ? ` (${u.fn})` : ''}`).join(', ');

  // Expand/collapse a talent row; on expand, resolve its linked talents so the
  // user can see who will be updated BEFORE saving.
  const toggleTalentRow = async (talent) => {
    const id = talent.id;
    if (expandedTalentId === id) { setExpandedTalentId(null); return; }
    setExpandedTalentId(id);
    if (!linkedPreview[id]) {
      setPreviewLoadingId(id);
      try {
        const members = await resolveLinkedInfo(id);
        setLinkedPreview((prev) => ({ ...prev, [id]: members }));
      } catch (e) {
        console.warn('Could not resolve linked talents:', e);
      } finally {
        setPreviewLoadingId(null);
      }
    }
  };

  // Save a single talent's own billing data. If the talent is linked to other
  // talents (same person, different function/rate), the SAME billing data is
  // applied to all linked records — fill once, update all 3-4 functions.
  const handleSaveIndividualTalent = async (talent) => {
    setSavingTalentId(talent.id);
    setTalentSaveMsg(prev => ({ ...prev, [talent.id]: null }));
    try {
      const form = talentForms[talent.id] || {};
      const updateData = {};
      TALENT_BILLING_KEYS.forEach((k) => { updateData[k] = form[k] ?? ''; });

      // Propagate to the talent's OWN relatedTalent chain only (transitive).
      // This is independent of agency grouping — related talents are the same
      // person's other function records, wherever they sit.
      const { allIds, ok, fail, updated } = await propagateBillingToLinked(talent.id, updateData);

      // Reflect the change locally for any affected talent shown in the agency.
      const idSet = new Set(allIds);
      setSelectedAgency(prev =>
        prev ? { ...prev, talents: prev.talents.map(t => idSet.has(String(t.id)) ? { ...t, ...updateData } : t) } : prev
      );
      setAgencies(prev => prev.map(a =>
        a.name === selectedAgency?.name
          ? { ...a, talents: a.talents.map(t => idSet.has(String(t.id)) ? { ...t, ...updateData } : t) }
          : a
      ));
      // Keep the open per-talent forms in sync for linked rows too.
      setTalentForms(prev => {
        const next = { ...prev };
        allIds.forEach((id) => { if (next[id]) next[id] = { ...next[id], ...updateData }; });
        return next;
      });

      const msg =
        fail === 0
          ? { type: 'ok', text: `Saved billing to ${ok} talent${ok > 1 ? 's' : ''}: ${describeUpdated(updated)}` }
          : { type: 'warn', text: `Saved ${ok}, ${fail} failed. Updated: ${describeUpdated(updated)}` };
      setTalentSaveMsg(prev => ({ ...prev, [talent.id]: msg }));
    } catch (err) {
      console.error(`Error saving talent ${talent.id}:`, err);
      setTalentSaveMsg(prev => ({ ...prev, [talent.id]: { type: 'warn', text: 'Failed to save' } }));
    } finally {
      setSavingTalentId(null);
    }
  };

  // Save agency info (applies to all talents under the agency)
  const handleSaveAgency = async (e) => {
    e.preventDefault();
    if (!selectedAgency) return;

    setIsSaving(true);
    setSaveMessage('');

    try {
      const newAgencyName = editForm.Agentur?.trim() || '';
      const oldAgencyName = selectedAgency.name;
      const agencyNameChanged = newAgencyName !== oldAgencyName;

      const updateData = {
        Agentur: newAgencyName,
        agency_vat_status: editForm.agency_vat_status,
        agency_tax_id: editForm.agency_tax_id,
        agency_vat_id: editForm.agency_vat_id,
        agency_bank_account: editForm.agency_bank_account,
        agency_email: editForm.agency_email,
        agency_address: editForm.agency_address,
        AP: editForm.AP,
        is_agency: true,
        agency_bill_separately: !!editForm.agency_bill_separately,
        agency_own_account: !!editForm.agency_own_account,
      };
      // Note: when billing separately, each talent's OWN VAT/billing data is
      // saved per talent (see handleSaveIndividualTalent) — NOT shared here.

      let successCount = 0;
      let failedCount = 0;

      for (const talent of selectedAgency.talents) {
        try {
          await talentService.updateTalent(talent.id, updateData);
          successCount++;
        } catch (err) {
          console.error(`Error updating talent ${talent.id}:`, err);
          failedCount++;
        }
      }

      // Update local state
      if (agencyNameChanged && newAgencyName === '') {
        // Agency name cleared - move talents to individual talents
        setAgencies(prev => prev.filter(a => a.name !== oldAgencyName));
        setTalentsWithoutAgency(prev => [
          ...prev,
          ...selectedAgency.talents.map(t => ({ ...t, ...updateData }))
        ]);
        setSaveMessage(`Removed agency name. ${successCount} talent(s) moved to Individual Talents.`);
        handleClose();
      } else if (agencyNameChanged) {
        // Agency name changed - update the agency entry
        setAgencies(prev => {
          // Check if new name already exists
          const existingAgency = prev.find(a => a.name === newAgencyName);
          if (existingAgency) {
            // Merge into existing agency
            return prev
              .filter(a => a.name !== oldAgencyName)
              .map(a =>
                a.name === newAgencyName
                  ? {
                      ...a,
                      ...updateData,
                      name: newAgencyName,
                      talents: [...a.talents, ...selectedAgency.talents.map(t => ({ ...t, ...updateData }))]
                    }
                  : a
              );
          } else {
            // Rename the agency
            return prev.map(a =>
              a.name === oldAgencyName
                ? { ...a, ...updateData, name: newAgencyName, talents: selectedAgency.talents.map(t => ({ ...t, ...updateData })) }
                : a
            ).sort((a, b) => a.name.localeCompare(b.name));
          }
        });
        setSelectedAgency(prev => ({ ...prev, ...updateData, name: newAgencyName }));
        if (failedCount === 0) {
          setSaveMessage(`Agency renamed to "${newAgencyName}". Updated ${successCount} talent(s).`);
        } else {
          setSaveMessage(`Agency renamed. Updated ${successCount} talent(s), ${failedCount} failed.`);
        }
      } else {
        // No name change - just update other fields
        setAgencies(prev => prev.map(a =>
          a.name === selectedAgency.name
            ? { ...a, ...updateData }
            : a
        ));
        setSelectedAgency(prev => ({ ...prev, ...updateData }));
        if (failedCount === 0) {
          setSaveMessage(`Updated ${successCount} talent(s) successfully!`);
        } else {
          setSaveMessage(`Updated ${successCount} talent(s), ${failedCount} failed.`);
        }
      }
    } catch (err) {
      console.error('Error saving agency info:', err);
      setSaveMessage('Error saving: ' + (err.message || 'Unknown error'));
    } finally {
      setIsSaving(false);
    }
  };

  // Save individual talent billing info
  const handleSaveTalent = async (e) => {
    e.preventDefault();
    if (!selectedTalent) return;

    setIsSaving(true);
    setSaveMessage('');

    try {
      const updateData = {
        Agentur: editForm.Agentur,
        vat_status: editForm.vat_status,
        tax_id: editForm.tax_id,
        vat_id: editForm.vat_id,
        bank_account: editForm.bank_account,
        country: editForm.country,
        company_name: editForm.company_name,
        address: editForm.address,
        // Persist agency data too (e.g. talent that is their own agency)
        is_agency: !!editForm.is_agency,
        agency_bill_separately: !!editForm.agency_bill_separately,
        agency_own_account: !!editForm.agency_own_account,
        AP: editForm.AP,
        agency_vat_status: editForm.agency_vat_status,
        agency_tax_id: editForm.agency_tax_id,
        agency_vat_id: editForm.agency_vat_id,
        agency_bank_account: editForm.agency_bank_account,
        agency_email: editForm.agency_email,
        agency_address: editForm.agency_address,
      };

      await talentService.updateTalent(selectedTalent.id, updateData);

      // Propagate the billing data ONLY (not Agentur) along this talent's
      // relatedTalent chain — same person's other function records.
      const billingOnly = {};
      TALENT_BILLING_KEYS.forEach((k) => { billingOnly[k] = editForm[k] ?? ''; });
      const { updated } = await propagateBillingToLinked(selectedTalent.id, billingOnly);
      const linkedNote = updated.length > 1 ? ` — billing applied to ${updated.length} linked talents: ${describeUpdated(updated)}` : '';

      // Update local state - if agency name was added, move to agencies list
      if (editForm.Agentur && editForm.Agentur.trim() !== '') {
        // Remove from individual talents
        setTalentsWithoutAgency(prev => prev.filter(t => t.id !== selectedTalent.id));

        // Add to agencies or create new agency group
        setAgencies(prev => {
          const existingAgency = prev.find(a => a.name === editForm.Agentur);
          if (existingAgency) {
            // Add to existing agency
            return prev.map(a =>
              a.name === editForm.Agentur
                ? { ...a, talents: [...a.talents, { ...selectedTalent, ...updateData }] }
                : a
            );
          } else {
            // Create new agency
            return [...prev, {
              name: editForm.Agentur,
              talents: [{ ...selectedTalent, ...updateData }],
              agency_vat_status: '',
              agency_tax_id: '',
              agency_vat_id: '',
              agency_bank_account: '',
              agency_email: '',
              AP: '',
            }].sort((a, b) => a.name.localeCompare(b.name));
          }
        });

        setSaveMessage('Talent updated and moved to agency: ' + editForm.Agentur);
        handleClose();
      } else {
        setTalentsWithoutAgency(prev => prev.map(t =>
          t.id === selectedTalent.id
            ? { ...t, ...updateData }
            : t
        ));
        setSelectedTalent(prev => ({ ...prev, ...updateData }));
        setSaveMessage(`Talent billing info updated successfully!${linkedNote}`);
      }
    } catch (err) {
      console.error('Error saving talent info:', err);
      setSaveMessage('Error saving: ' + (err.message || 'Unknown error'));
    } finally {
      setIsSaving(false);
    }
  };

  const handleClose = () => {
    setSelectedAgency(null);
    setSelectedTalent(null);
    setEditMode(null);
    setEditForm({});
    setSaveMessage('');
  };

  if (loading) {
    return (
      <div className="content-wrapper page">
        <div className="content-wrapper">
          <h1>Billing Management</h1>
          <p>Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="content-wrapper page">
      <div className="content-wrapper">
        <h1>Billing Management</h1>
        <p className="talent-list-simple-intro">
          Manage billing info for agencies and individual talents.
        </p>

        {error && (
          <div className="talent-list-simple-message error">
            {error}
          </div>
        )}

        {locationOptions.length > 0 && (
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap', marginBottom: '20px' }}>
            <Select
              value={filterLocation}
              onChange={setFilterLocation}
              options={locationOptions}
              isClearable
              placeholder="Filter by Location"
              styles={customStyles}
            />
            {filterLocation && (
              <span style={{ fontSize: '12px', color: '#666' }}>
                Agencies with at least one talent at {filterLocation.label}, and individual
                talents working there
              </span>
            )}
          </div>
        )}

        <div className="agency-mgmt-layout">
          {/* Left Panel - Lists */}
          <div className="agency-mgmt-list">
            {/* Agencies Section */}
            <h2>Agencies ({visibleAgencies.length}{filterLocation ? ` of ${agencies.length}` : ''})</h2>
            <p style={{ fontSize: '12px', color: '#666', marginBottom: '10px' }}>
              Talents grouped by agency. Updates apply to all talents.
            </p>
            <div style={{
              maxHeight: '300px',
              overflowY: 'auto',
              border: '1px solid #ddd',
              borderRadius: '5px',
              marginBottom: '20px'
            }}>
              {visibleAgencies.length === 0 ? (
                <p style={{ padding: '15px', textAlign: 'center', color: '#666' }}>
                  {filterLocation ? `No agencies with talents at ${filterLocation.label}` : 'No agencies found'}
                </p>
              ) : (
                visibleAgencies.map((agency, index) => (
                  <div
                    key={index}
                    onClick={() => handleSelectAgency(agency)}
                    style={{
                      padding: '10px 15px',
                      borderBottom: '1px solid #eee',
                      cursor: 'pointer',
                      backgroundColor: selectedAgency?.name === agency.name ? '#e3f2fd' : 'white',
                      transition: 'background-color 0.2s'
                    }}
                    onMouseEnter={(e) => {
                      if (selectedAgency?.name !== agency.name) {
                        e.currentTarget.style.backgroundColor = '#f5f5f5';
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (selectedAgency?.name !== agency.name) {
                        e.currentTarget.style.backgroundColor = 'white';
                      }
                    }}
                  >
                    <div style={{ fontWeight: '500' }}>{agency.name}</div>
                    <div style={{ fontSize: '11px', color: '#666', marginTop: '3px' }}>
                      {agency.talents.length} talent(s) · {agencyLocationLabel(agency)}
                    </div>
                  </div>
                ))
              )}
            </div>

            {/* Individual Talents Section */}
            <h2>Individual Talents ({visibleIndividuals.length}{filterLocation ? ` of ${talentsWithoutAgency.length}` : ''})</h2>
            <p style={{ fontSize: '12px', color: '#666', marginBottom: '10px' }}>
              Talents without an agency. Update their own billing info.
            </p>
            <div style={{
              maxHeight: '300px',
              overflowY: 'auto',
              border: '1px solid #ddd',
              borderRadius: '5px'
            }}>
              {visibleIndividuals.length === 0 ? (
                <p style={{ padding: '15px', textAlign: 'center', color: '#666' }}>
                  {filterLocation ? `No individual talents at ${filterLocation.label}` : 'No individual talents found'}
                </p>
              ) : (
                visibleIndividuals.map((talent, index) => (
                  <div
                    key={index}
                    onClick={() => handleSelectTalent(talent)}
                    style={{
                      padding: '10px 15px',
                      borderBottom: '1px solid #eee',
                      cursor: 'pointer',
                      backgroundColor: selectedTalent?.id === talent.id ? '#fff3e0' : 'white',
                      transition: 'background-color 0.2s'
                    }}
                    onMouseEnter={(e) => {
                      if (selectedTalent?.id !== talent.id) {
                        e.currentTarget.style.backgroundColor = '#f5f5f5';
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (selectedTalent?.id !== talent.id) {
                        e.currentTarget.style.backgroundColor = 'white';
                      }
                    }}
                  >
                    <div style={{ fontWeight: '500' }}>{talent.Name || talent.name}</div>
                    <div style={{ fontSize: '11px', color: '#666', marginTop: '3px' }}>
                      {talent.E_Mail || talent.email || 'No email'} · {talentLocationLabel(talent)}
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>

          {/* Right Panel - Edit Form */}
          <div className="agency-mgmt-detail">
            {editMode === 'agency' && selectedAgency ? (
              <>
                <h2>Agency: {selectedAgency.name}</h2>
                <p style={{ color: '#666', marginBottom: '15px' }}>
                  Updating will apply to {selectedAgency.talents.length} talent(s):
                  <span style={{ fontSize: '12px', display: 'block', marginTop: '5px' }}>
                    {selectedAgency.talents.map(t => t.Name || t.name).join(', ')}
                  </span>
                </p>

                {saveMessage && (
                  <div style={{
                    padding: '10px',
                    marginBottom: '15px',
                    borderRadius: '5px',
                    backgroundColor: saveMessage.includes('Error') || saveMessage.includes('failed') ? '#ffebee' : '#e8f5e9',
                    color: saveMessage.includes('Error') || saveMessage.includes('failed') ? '#c62828' : '#2e7d32'
                  }}>
                    {saveMessage}
                  </div>
                )}

                <form onSubmit={handleSaveAgency}>
                  <div className="form-group">
                    <label>Agency Name</label>
                    <input
                      type="text"
                      name="Agentur"
                      value={editForm.Agentur || ''}
                      onChange={handleInputChange}
                      placeholder="Agency name"
                    />
                    <small style={{ color: '#666', fontSize: '11px' }}>
                      Changing the name will update all talents under this agency.
                    </small>
                  </div>

                  <div className="form-group">
                    <label>AP (Agency Percentage)</label>
                    <input
                      type="text"
                      name="AP"
                      value={editForm.AP || ''}
                      onChange={handleInputChange}
                      placeholder="e.g. 15"
                    />
                  </div>

                  <div className="form-group">
                    <label>Agency VAT Status <InfoTip text={TAX_TIP} /></label>
                    <select
                      name="agency_vat_status"
                      value={editForm.agency_vat_status || ''}
                      onChange={handleInputChange}
                    >
                      <option value="">— Select —</option>
                      <option value="with_vat">With VAT (19%)</option>
                      <option value="with_vat_7">With VAT (7%)</option>
                      <option value="without_vat">Without VAT</option>
                    </select>
                  </div>

                  <div className="form-group">
                    <label>Agency Tax Number / Steuer Nummer (xxx/xxx/xxx) <InfoTip text={VAT_TIP} /></label>
                    <input
                      type="text"
                      name="agency_tax_id"
                      value={editForm.agency_tax_id || ''}
                      onChange={handleInputChange}
                      placeholder="xxx/xxx/xxx"
                    />
                  </div>

                  <div className="form-group">
                    <label>Agency VAT ID / Ust-Nr (DE XXXXXXXXX) <InfoTip text={VAT_TIP} /></label>
                    <input
                      type="text"
                      name="agency_vat_id"
                      value={editForm.agency_vat_id || ''}
                      onChange={handleInputChange}
                      placeholder="DE XXXXXXXXX"
                    />
                  </div>

                  <div className="form-group">
                    <label>Agency Bank Account</label>
                    <textarea
                      name="agency_bank_account"
                      value={editForm.agency_bank_account || ''}
                      onChange={handleInputChange}
                      rows={3}
                      placeholder="Bank Name, IBAN, BIC"
                    />
                  </div>

                  <div className="form-group">
                    <label>Agency Email</label>
                    <input
                      type="email"
                      name="agency_email"
                      value={editForm.agency_email || ''}
                      onChange={handleInputChange}
                    />
                  </div>

                  <div className="form-group">
                    <label>Agency Address (Adresse)</label>
                    <textarea
                      name="agency_address"
                      value={editForm.agency_address || ''}
                      onChange={handleInputChange}
                      rows={2}
                      placeholder="Straße Hausnr., PLZ Ort, Land"
                    />
                    <small style={{ color: '#666', fontSize: '11px' }}>
                      Required on credit notes (German law). E.g.: Musterstraße 12, 20099 Hamburg
                    </small>
                  </div>

                  <div className="form-group talent-edit-checkbox">
                    <label>
                      <input
                        type="checkbox"
                        name="agency_bill_separately"
                        checked={!!editForm.agency_bill_separately}
                        onChange={handleInputChange}
                      />
                      {' '}Bill separately (talent CN + agency provision CN)
                    </label>
                    <small className="talent-edit-hint" style={{ display: 'block', marginTop: '4px', color: '#666', fontSize: '12px' }}>
                      When unchecked (default, client-approved): ONE credit note to the agency — talent lines with talent VAT plus the AP provision position with agency VAT (mixed rates supported, tax IDs shown per line). When checked: two linked credit notes (talent + agency provision) — only for agencies that explicitly require separate documents.
                    </small>
                  </div>

                  <div className="form-group talent-edit-checkbox">
                    <label>
                      <input
                        type="checkbox"
                        name="agency_own_account"
                        checked={!!editForm.agency_own_account}
                        onChange={handleInputChange}
                      />
                      {' '}Agency bills on its own account (Agentur rechnet auf eigene Rechnung ab)
                    </label>
                    <small className="talent-edit-hint" style={{ display: 'block', marginTop: '4px', color: '#666', fontSize: '12px' }}>
                      When checked, the credit note texts no longer say the agency acts &quot;im Namen und im Auftrag des Talents&quot; — the agency bills the talent&apos;s services in its own name and for its own account. Mutually exclusive with &quot;Bill separately&quot;.
                    </small>
                  </div>

                  <div className="button-group" style={{ marginTop: '20px' }}>
                    <button type="submit" className="button primary" disabled={isSaving}>
                      {isSaving ? `Saving agency to ${selectedAgency.talents.length} talent(s)…` : 'Save Agency'}
                    </button>
                    <button type="button" className="button secondary" onClick={handleClose} disabled={isSaving}>
                      Close
                    </button>
                  </div>
                  <small style={{ color: '#666', fontSize: '11px', display: 'block', marginTop: '6px', marginBottom: '10px' }}>
                    “Save Agency” saves the agency-level data above to all talents in this agency. Each talent’s own VAT/billing is saved with its own “Save this talent” button below.
                  </small>

                  <>
                      <h3 className="talent-edit-section-title" style={{ marginTop: '20px', marginBottom: '10px' }}>
                        Talent VAT / Billing — per talent ({selectedAgency.talents.length})
                      </h3>
                      <p style={{ fontSize: '12px', color: '#666', marginBottom: '12px' }}>
                        Each talent keeps the shared agency data above <strong>plus their own</strong> VAT &amp; billing data — always fill this in for every talent (used for the talent credit note{editForm.agency_bill_separately ? ', and required when billing separately' : ''}). Click a talent to edit and save their data individually.
                        {' '}<strong><Icon.link size={12} /> Linked</strong> talents (same person, different function/rate) share one set of billing data: fill it once and saving updates all linked functions.
                      </p>
                      {agencyDetailLoading && (
                        <p style={{ fontSize: '12px', color: '#1565c0', marginBottom: '8px' }}>Loading linked talent data…</p>
                      )}
                      <div style={{ border: '1px solid #ddd', borderRadius: '5px', marginBottom: '10px' }}>
                        {selectedAgency.talents.map((t) => {
                          const form = talentForms[t.id] || {};
                          const isOpen = expandedTalentId === t.id;
                          const hasData = !!(form.vat_status || form.tax_id || form.vat_id || form.address || form.bank_account);
                          const fn = t.Funktion || t.function || '';
                          const dayRate = t.Tagessatz || t.rate || t.dayRate || '';
                          return (
                            <div key={t.id} style={{ borderBottom: '1px solid #eee' }}>
                              <div
                                onClick={() => toggleTalentRow(t)}
                                style={{
                                  padding: '10px 15px',
                                  cursor: 'pointer',
                                  display: 'flex',
                                  justifyContent: 'space-between',
                                  alignItems: 'center',
                                  backgroundColor: isOpen ? '#e3f2fd' : 'white',
                                }}
                              >
                                <div>
                                  <div style={{ fontWeight: 500 }}>
                                    {t.Name || t.name}
                                    {(fn || dayRate) && (
                                      <span style={{ fontWeight: 400, fontSize: '12px', color: '#333', marginLeft: '8px' }}>
                                        {fn}{fn && dayRate ? ' · ' : ''}{dayRate ? `€${dayRate}/day` : ''}
                                      </span>
                                    )}
                                    {(linkedGroups[String(t.id)]?.length > 1) && (
                                      <span
                                        title={`Linked to ${linkedGroups[String(t.id)].length - 1} other function(s) — saving updates all of them`}
                                        style={{ marginLeft: '8px', fontSize: '10px', fontWeight: 600, color: '#1565c0', background: '#e3f2fd', padding: '2px 7px', borderRadius: '10px', display: 'inline-flex', alignItems: 'center', gap: '4px' }}
                                      >
                                        <Icon.link size={11} /> linked ×{linkedGroups[String(t.id)].length}
                                      </span>
                                    )}
                                  </div>
                                  <div style={{ fontSize: '11px', color: '#666' }}>
                                    {t.E_Mail || t.email || 'No email'} · {talentLocationLabel(t)}
                                  </div>
                                </div>
                                <div style={{ fontSize: '11px', color: hasData ? '#2e7d32' : '#999', display: 'inline-flex', alignItems: 'center', gap: '5px' }}>
                                  {hasData ? <Icon.dotFilled /> : <Icon.dotEmpty />}
                                  {hasData ? 'has data' : 'empty'}
                                  <span style={{ color: '#999', marginLeft: '2px' }}><Icon.chevron open={isOpen} /></span>
                                </div>
                              </div>
                              {isOpen && (
                                <div style={{ padding: '15px', backgroundColor: '#fafafa' }}>
                                  {/* Linked-talent preview: who will also be updated */}
                                  {previewLoadingId === t.id ? (
                                    <div style={{ fontSize: '12px', color: '#1565c0', marginBottom: '12px' }}>Checking linked talents…</div>
                                  ) : linkedPreview[t.id] ? (
                                    linkedPreview[t.id].length > 1 ? (
                                      <div style={{ fontSize: '12px', marginBottom: '12px', padding: '8px 10px', background: '#e3f2fd', borderRadius: '4px', color: '#0d47a1' }}>
                                        <Icon.link size={12} /> Saving updates billing for these {linkedPreview[t.id].length} linked talents:
                                        <div style={{ marginTop: '4px' }}>
                                          {linkedPreview[t.id].map((m) => (
                                            <span key={m.id} style={{ display: 'inline-block', margin: '2px 4px 0 0', padding: '1px 6px', background: '#fff', borderRadius: '10px', border: '1px solid #bbdefb' }}>
                                              {m.name}{m.fn ? ` · ${m.fn}` : ''}
                                            </span>
                                          ))}
                                        </div>
                                      </div>
                                    ) : (
                                      <div style={{ fontSize: '12px', marginBottom: '12px', padding: '8px 10px', background: '#fff3e0', borderRadius: '4px', color: '#8a6d3b' }}>
                                        Not linked to any other talent — saving updates only this one. To link, use “Link talents” in the talent list.
                                      </div>
                                    )
                                  ) : null}
                                  <div className="form-group">
                                    <label>VAT Status <InfoTip text={TAX_TIP} /></label>
                                    <select name="vat_status" value={form.vat_status || ''} onChange={(e) => handleTalentFormChange(t.id, e)}>
                                      <option value="">— Select —</option>
                                      <option value="with_vat">With VAT (19%)</option>
                                      <option value="with_vat_7">With VAT (7%)</option>
                                      <option value="without_vat">Without VAT</option>
                                      <option value="kleinunternehmer">Kleinunternehmer</option>
                                      <option value="auslander">Ausländer (abroad)</option>
                                    </select>
                                  </div>
                                  <div className="form-group">
                                    <label>Tax Number / Steuer Nummer (xxx/xxx/xxx) <InfoTip text={VAT_TIP} /></label>
                                    <input type="text" name="tax_id" value={form.tax_id || ''} onChange={(e) => handleTalentFormChange(t.id, e)} placeholder="xxx/xxx/xxx" />
                                  </div>
                                  <div className="form-group">
                                    <label>VAT ID / Ust-Nr (DE XXXXXXXXX) <InfoTip text={VAT_TIP} /></label>
                                    <input type="text" name="vat_id" value={form.vat_id || ''} onChange={(e) => handleTalentFormChange(t.id, e)} placeholder="DE XXXXXXXXX" />
                                  </div>
                                  <div className="form-group">
                                    <label>Bank Account</label>
                                    <textarea name="bank_account" value={form.bank_account || ''} onChange={(e) => handleTalentFormChange(t.id, e)} rows={3} placeholder="Bank Name, IBAN, BIC" />
                                  </div>
                                  <div className="form-group">
                                    <label>Country</label>
                                    <input type="text" name="country" value={form.country || ''} onChange={(e) => handleTalentFormChange(t.id, e)} placeholder="e.g. Germany" />
                                  </div>
                                  <div className="form-group">
                                    <label>Company Name</label>
                                    <input type="text" name="company_name" value={form.company_name || ''} onChange={(e) => handleTalentFormChange(t.id, e)} />
                                  </div>
                                  <div className="form-group">
                                    <label>Talent Address (Adresse)</label>
                                    <textarea name="address" value={form.address || ''} onChange={(e) => handleTalentFormChange(t.id, e)} rows={2} placeholder="Straße Hausnr., PLZ Ort, Land" />
                                    <small style={{ color: '#666', fontSize: '11px' }}>
                                      Required on credit notes. E.g.: Musterstraße 12, 20099 Hamburg
                                    </small>
                                  </div>
                                  <div className="button-group" style={{ marginTop: '10px', display: 'flex', alignItems: 'center', gap: '10px' }}>
                                    <button
                                      type="button"
                                      className="button primary"
                                      disabled={savingTalentId === t.id}
                                      onClick={() => handleSaveIndividualTalent(t)}
                                    >
                                      {savingTalentId === t.id ? 'Saving...' : 'Save this talent'}
                                    </button>
                                    {talentSaveMsg[t.id] && (
                                      <span style={{ fontSize: '13px', display: 'inline-flex', alignItems: 'center', gap: '5px', color: talentSaveMsg[t.id].type === 'warn' ? '#c62828' : '#2e7d32' }}>
                                        {talentSaveMsg[t.id].type === 'warn' ? <Icon.warn /> : <Icon.check />}
                                        {talentSaveMsg[t.id].text}
                                      </span>
                                    )}
                                  </div>
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </>
                </form>
              </>
            ) : editMode === 'talent' && selectedTalent ? (
              <>
                <h2>Talent: {selectedTalent.Name || selectedTalent.name}</h2>
                <p style={{ color: '#666', marginBottom: '15px' }}>
                  {selectedTalent.E_Mail || selectedTalent.email}
                </p>

                {saveMessage && (
                  <div style={{
                    padding: '10px',
                    marginBottom: '15px',
                    borderRadius: '5px',
                    backgroundColor: saveMessage.includes('Error') ? '#ffebee' : '#e8f5e9',
                    color: saveMessage.includes('Error') ? '#c62828' : '#2e7d32'
                  }}>
                    {saveMessage}
                  </div>
                )}

                <form onSubmit={handleSaveTalent}>
                  <div className="form-group">
                    <label>Agency Name</label>
                    <input
                      type="text"
                      name="Agentur"
                      value={editForm.Agentur || ''}
                      onChange={handleInputChange}
                      placeholder="Enter agency name if applicable"
                    />
                    <small style={{ color: '#666', fontSize: '11px' }}>
                      If you add an agency name, this talent will be grouped under that agency.
                    </small>
                  </div>

                  <div className="form-group talent-edit-checkbox">
                    <label>
                      <input
                        type="checkbox"
                        name="is_agency"
                        checked={!!editForm.is_agency}
                        onChange={handleInputChange}
                      />
                      {' '}Is agency (this talent is / acts as an agency)
                    </label>
                  </div>

                  {editForm.is_agency && (
                    <>
                      <h3 className="talent-edit-section-title" style={{ marginTop: '10px', marginBottom: '10px' }}>Agency data</h3>
                      <div className="form-group">
                        <label>AP (Agency Percentage)</label>
                        <input type="text" name="AP" value={editForm.AP || ''} onChange={handleInputChange} placeholder="e.g. 15" />
                      </div>
                      <div className="form-group talent-edit-checkbox">
                        <label>
                          <input type="checkbox" name="agency_bill_separately" checked={!!editForm.agency_bill_separately} onChange={handleInputChange} />
                          {' '}Bill separately (talent CN + agency provision CN)
                        </label>
                      </div>
                      <div className="form-group">
                        <label>Agency VAT Status <InfoTip text={TAX_TIP} /></label>
                        <select name="agency_vat_status" value={editForm.agency_vat_status || ''} onChange={handleInputChange}>
                          <option value="">— Select —</option>
                          <option value="with_vat">With VAT (19%)</option>
                          <option value="with_vat_7">With VAT (7%)</option>
                          <option value="without_vat">Without VAT</option>
                        </select>
                      </div>
                      <div className="form-group">
                        <label>Agency Tax Number / Steuer Nummer (xxx/xxx/xxx) <InfoTip text={VAT_TIP} /></label>
                        <input type="text" name="agency_tax_id" value={editForm.agency_tax_id || ''} onChange={handleInputChange} placeholder="xxx/xxx/xxx" />
                      </div>
                      <div className="form-group">
                        <label>Agency VAT ID / Ust-Nr (DE XXXXXXXXX) <InfoTip text={VAT_TIP} /></label>
                        <input type="text" name="agency_vat_id" value={editForm.agency_vat_id || ''} onChange={handleInputChange} placeholder="DE XXXXXXXXX" />
                      </div>
                      <div className="form-group">
                        <label>Agency Bank Account</label>
                        <textarea name="agency_bank_account" value={editForm.agency_bank_account || ''} onChange={handleInputChange} rows={3} placeholder="Bank Name, IBAN, BIC" />
                      </div>
                      <div className="form-group">
                        <label>Agency Email</label>
                        <input type="email" name="agency_email" value={editForm.agency_email || ''} onChange={handleInputChange} />
                      </div>
                      <div className="form-group">
                        <label>Agency Address (Adresse)</label>
                        <textarea name="agency_address" value={editForm.agency_address || ''} onChange={handleInputChange} rows={2} placeholder="Straße Hausnr., PLZ Ort, Land" />
                      </div>
                      <h3 className="talent-edit-section-title" style={{ marginTop: '10px', marginBottom: '10px' }}>Talent billing</h3>
                    </>
                  )}

                  <div className="form-group">
                    <label>VAT Status <InfoTip text={TAX_TIP} /></label>
                    <select
                      name="vat_status"
                      value={editForm.vat_status || ''}
                      onChange={handleInputChange}
                    >
                      <option value="">— Select —</option>
                      <option value="with_vat">With VAT (19%)</option>
                      <option value="with_vat_7">With VAT (7%)</option>
                      <option value="without_vat">Without VAT</option>
                      <option value="kleinunternehmer">Kleinunternehmer</option>
                      <option value="auslander">Ausländer (abroad)</option>
                    </select>
                  </div>

                  <div className="form-group">
                    <label>Tax Number / Steuer Nummer (xxx/xxx/xxx) <InfoTip text={VAT_TIP} /></label>
                    <input
                      type="text"
                      name="tax_id"
                      value={editForm.tax_id || ''}
                      onChange={handleInputChange}
                      placeholder="xxx/xxx/xxx"
                    />
                  </div>

                  <div className="form-group">
                    <label>VAT ID / Ust-Nr (DE XXXXXXXXX) <InfoTip text={VAT_TIP} /></label>
                    <input
                      type="text"
                      name="vat_id"
                      value={editForm.vat_id || ''}
                      onChange={handleInputChange}
                      placeholder="DE XXXXXXXXX"
                    />
                  </div>

                  <div className="form-group">
                    <label>Bank Account</label>
                    <textarea
                      name="bank_account"
                      value={editForm.bank_account || ''}
                      onChange={handleInputChange}
                      rows={3}
                      placeholder="Bank Name, IBAN, BIC"
                    />
                  </div>

                  <div className="form-group">
                    <label>Country</label>
                    <input
                      type="text"
                      name="country"
                      value={editForm.country || ''}
                      onChange={handleInputChange}
                      placeholder="e.g. Germany"
                    />
                  </div>

                  <div className="form-group">
                    <label>Company Name</label>
                    <input
                      type="text"
                      name="company_name"
                      value={editForm.company_name || ''}
                      onChange={handleInputChange}
                    />
                  </div>

                  <div className="form-group">
                    <label>Address (Adresse)</label>
                    <textarea
                      name="address"
                      value={editForm.address || ''}
                      onChange={handleInputChange}
                      rows={2}
                      placeholder="Straße Hausnr., PLZ Ort, Land"
                    />
                    <small style={{ color: '#666', fontSize: '11px' }}>
                      Required on credit notes (German law). E.g.: Musterstraße 12, 20099 Hamburg
                    </small>
                  </div>

                  <div className="button-group" style={{ marginTop: '20px' }}>
                    <button type="submit" className="button primary" disabled={isSaving}>
                      {isSaving ? 'Saving...' : 'Save'}
                    </button>
                    <button type="button" className="button secondary" onClick={handleClose} disabled={isSaving}>
                      Close
                    </button>
                  </div>
                </form>
              </>
            ) : (
              <div style={{
                padding: '40px',
                textAlign: 'center',
                color: '#666',
                backgroundColor: '#f9f9f9',
                borderRadius: '5px'
              }}>
                <p>Select an agency or talent from the list to edit billing info.</p>
                <p style={{ fontSize: '12px', marginTop: '10px' }}>
                  <strong>Agencies:</strong> Changes apply to all talents under the agency.<br />
                  <strong>Individual Talents:</strong> Changes apply only to that talent.
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default AgencyManagement;
